import { isLimitError } from "@/utils/limitErrors"
import { BotIcon, ChevronRight, Loader2, PlusIcon } from "lucide-react"
import * as React from "react"

import { toast } from "@/components/common/Toast"
import {
  CONVERSATION_TITLE_MAX_LENGTH,
  CONVERSATIONS_LOAD_ERROR_TOAST_ID,
  SIDEBAR_ICON_SIZE,
} from "@/constants/UiConstants"
import { useOnboarding } from "@/features/onboarding"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useCreateChatConversation,
  useGetChatConversations,
  useRenameChatConversation,
} from "@/services/api/chatConversationService"
import { useLocation, useNavigate } from "react-router"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import { ConversationActions } from "./ConversationActions"

const AI_ASSISTANT_OPEN_STORAGE_KEY = "turnus-ai-assistant-open"

type ChatSidebarItemProps = {
  isSidebarOpen?: boolean
  /**
   * When true, clicking the main row toggles the conversation list open/closed
   * instead of navigating to /chat/history (used by the V2 sidebar). Defaults
   * to false, preserving today's navigation behavior for the old sidebar.
   */
  rowClickToggles?: boolean
}

export function ChatSidebarItem({
  isSidebarOpen,
  rowClickToggles = false,
}: ChatSidebarItemProps) {
  const { translate } = useTranslation()
  const {
    handleHighlightedAction,
    getCurrentTour,
    currentStepIndex,
    isActive,
  } = useOnboarding()
  const navigate = useNavigate()
  const location = useLocation()

  const [isAssistantOpen, setIsAssistantOpen] = React.useState(() => {
    const saved = localStorage.getItem(AI_ASSISTANT_OPEN_STORAGE_KEY)
    if (saved === null) {
      return true
    }
    return saved === "true"
  })

  const {
    data: sidebarConversations,
    isPending: isConversationsPending,
    isError: isConversationsError,
  } = useGetChatConversations(1, 5, Boolean(isSidebarOpen))
  const createConversation = useCreateChatConversation()
  const renameMutation = useRenameChatConversation()

  const [editingConversationId, setEditingConversationId] = React.useState<
    string | null
  >(null)
  const [editingTitle, setEditingTitle] = React.useState("")
  const [isRenameSubmitting, setIsRenameSubmitting] = React.useState(false)
  const editInputRef = React.useRef<HTMLInputElement | null>(null)
  const isRenameSubmittingRef = React.useRef(false)

  const focusAndSelectEditInput = React.useCallback(() => {
    const input = editInputRef.current
    if (!input) {
      return
    }

    input.focus()
    input.setSelectionRange(0, input.value.length)
  }, [])

  const activeConversationId = React.useMemo(() => {
    const nextSearchParams = new URLSearchParams(location.search)
    return nextSearchParams.get("conversation")
  }, [location.search])

  React.useEffect(() => {
    if (isConversationsError) {
      toast.error(translate("AI_CHAT/HISTORY/LIST_ERROR"), {
        id: CONVERSATIONS_LOAD_ERROR_TOAST_ID,
      })
    }
  }, [isConversationsError, translate])

  React.useEffect(() => {
    localStorage.setItem(AI_ASSISTANT_OPEN_STORAGE_KEY, String(isAssistantOpen))
  }, [isAssistantOpen])

  React.useEffect(() => {
    if (!editingConversationId) {
      return
    }

    const rafId = window.requestAnimationFrame(() => {
      focusAndSelectEditInput()
    })
    const timeoutId = window.setTimeout(() => {
      focusAndSelectEditInput()
    }, 0)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.clearTimeout(timeoutId)
    }
  }, [editingConversationId, focusAndSelectEditInput])

  const handleAssistantRowClick = () => {
    handleHighlightedAction("navItem4")
    if (rowClickToggles) {
      setIsAssistantOpen((prev) => !prev)
      return
    }
    navigate("/chat/history")
  }

  const handleStartNewChat = async () => {
    const currentTour = getCurrentTour()
    const isAiChatTourActive = isActive && currentTour?.id === "ai-chat"

    if (isAiChatTourActive) {
      const currentStep = currentTour.steps[currentStepIndex]
      if (currentStep?.targetElementId === "ai-chat-new-conv-button") {
        handleHighlightedAction("ai-chat-new-conv-button", {
          performNavigation: true,
        })
      } else {
        navigate("/chat")
      }
      return
    }

    if (createConversation.isPending) {
      return
    }

    try {
      const conversation = await createConversation.mutateAsync({})
      navigate(`/chat?conversation=${conversation.id}`)
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("AI_CHAT/HISTORY/CREATE_ERROR"))
      }
    }
  }

  const startInlineRename = (conversationId: string, title: string) => {
    setEditingConversationId(conversationId)
    setEditingTitle(title)
  }

  const cancelInlineRename = React.useCallback(() => {
    if (isRenameSubmittingRef.current) {
      return
    }

    setEditingConversationId(null)
    setEditingTitle("")
  }, [])

  React.useEffect(() => {
    if (!editingConversationId) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }

      if (editInputRef.current?.contains(target)) {
        return
      }

      cancelInlineRename()
    }

    document.addEventListener("mousedown", handlePointerDown, true)
    document.addEventListener("touchstart", handlePointerDown, true)

    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true)
      document.removeEventListener("touchstart", handlePointerDown, true)
    }
  }, [editingConversationId, cancelInlineRename])

  const saveInlineRename = async (
    conversationId: string,
    currentTitle: string,
  ) => {
    if (isRenameSubmittingRef.current) {
      return
    }

    const trimmedTitle = editingTitle.trim()
    if (!trimmedTitle) {
      toast.error(translate("AI_CHAT/HISTORY/RENAME_EMPTY_ERROR"))
      return
    }

    if (trimmedTitle === currentTitle.trim()) {
      cancelInlineRename()
      return
    }

    try {
      isRenameSubmittingRef.current = true
      setIsRenameSubmitting(true)
      await renameMutation.mutateAsync({
        conversationId,
        payload: { title: trimmedTitle },
      })
      toast.success(translate("AI_CHAT/HISTORY/RENAME_SUCCESS"))
      setEditingConversationId(null)
      setEditingTitle("")
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("AI_CHAT/HISTORY/RENAME_ERROR"))
      }
    } finally {
      isRenameSubmittingRef.current = false
      setIsRenameSubmitting(false)
    }
  }

  return (
    <Collapsible
      asChild
      open={isAssistantOpen}
      onOpenChange={setIsAssistantOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <SidebarMenuButton
          id="navItem4"
          onClick={handleAssistantRowClick}
          isActive={location.pathname.startsWith("/chat")}
        >
          <BotIcon
            style={{ height: SIDEBAR_ICON_SIZE, width: SIDEBAR_ICON_SIZE }}
          />
          <span>{translate("AI_CHAT/ASSISTANT")}</span>
        </SidebarMenuButton>
        <SidebarMenuAction
          id="ai-chat-new-conv-button"
          className="right-7 disabled:pointer-events-none disabled:opacity-50"
          onClick={() => {
            void handleStartNewChat()
          }}
          disabled={createConversation.isPending}
          aria-label={translate("AI_CHAT/NEW_CONVERSATION")}
          aria-busy={createConversation.isPending}
        >
          {createConversation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PlusIcon className="h-4 w-4" />
          )}
          <span className="sr-only">
            {translate("AI_CHAT/NEW_CONVERSATION")}
          </span>
        </SidebarMenuAction>
        <CollapsibleTrigger asChild>
          <SidebarMenuAction className="right-1 data-[state=open]:rotate-90">
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">
              {translate("AI_CHAT/HISTORY/OPTIONS")}
            </span>
          </SidebarMenuAction>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <SidebarMenuSub>
            {isConversationsPending ? (
              <>
                <SidebarMenuSubItem>
                  <SidebarMenuSkeleton />
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSkeleton />
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSkeleton />
                </SidebarMenuSubItem>
              </>
            ) : null}

            {!isConversationsPending && isConversationsError ? (
              <SidebarMenuSubItem>
                <span className="px-2 py-1 text-xs text-sidebar-foreground/70">
                  {translate("AI_CHAT/HISTORY/LIST_ERROR")}
                </span>
              </SidebarMenuSubItem>
            ) : null}

            {!isConversationsPending &&
            !isConversationsError &&
            (sidebarConversations?.conversations?.length || 0) === 0 ? (
              <SidebarMenuSubItem>
                <span className="px-2 py-1 text-xs text-sidebar-foreground/70">
                  {translate("AI_CHAT/HISTORY/EMPTY")}
                </span>
              </SidebarMenuSubItem>
            ) : null}

            {(sidebarConversations?.conversations || []).map((conversation) => {
              const isActive = activeConversationId === conversation.id
              const isEditing = editingConversationId === conversation.id

              return (
                <SidebarMenuSubItem key={conversation.id}>
                  <div
                    className={`group/item flex w-full items-center gap-1 rounded-md ${
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                    }`}
                  >
                    {isEditing ? (
                      <div className="min-w-0 flex-1 px-2">
                        <div className="relative">
                          <Input
                            ref={editInputRef}
                            value={editingTitle}
                            onChange={(event) =>
                              setEditingTitle(event.target.value)
                            }
                            onBlur={cancelInlineRename}
                            onFocus={() => {
                              focusAndSelectEditInput()
                              window.setTimeout(() => {
                                focusAndSelectEditInput()
                              }, 0)
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault()
                                if (isRenameSubmittingRef.current) {
                                  return
                                }
                                void saveInlineRename(
                                  conversation.id,
                                  conversation.title,
                                )
                              }
                              if (event.key === "Escape") {
                                event.preventDefault()
                                cancelInlineRename()
                              }
                            }}
                            className="h-7 border-sidebar-border bg-sidebar px-2 pr-8 text-sm"
                            disabled={isRenameSubmitting}
                            maxLength={CONVERSATION_TITLE_MAX_LENGTH}
                            autoFocus
                          />
                          {isRenameSubmitting ? (
                            <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <SidebarMenuSubButton
                        asChild
                        isActive={isActive}
                        className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent"
                      >
                        <button
                          type="button"
                          className="min-w-0 flex-1"
                          title={conversation.title}
                          onClick={() =>
                            navigate(`/chat?conversation=${conversation.id}`)
                          }
                        >
                          <span>{conversation.title}</span>
                        </button>
                      </SidebarMenuSubButton>
                    )}
                    {!isEditing ? (
                      <ConversationActions
                        compact
                        showOnHover
                        conversationId={conversation.id}
                        title={conversation.title}
                        onRenameInline={() =>
                          startInlineRename(conversation.id, conversation.title)
                        }
                      />
                    ) : null}
                  </div>
                </SidebarMenuSubItem>
              )
            })}

            {rowClickToggles ? (
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  asChild
                  className="text-sidebar-foreground/70"
                >
                  <button
                    type="button"
                    className="w-full"
                    onClick={() => navigate("/chat/history")}
                  >
                    <span>{translate("SIDEBAR/ALL_CONVERSATIONS")}</span>
                  </button>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ) : null}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
